package com.example.mystybag.common.views

import androidx.compose.foundation.background
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.text.KeyboardOptions
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.remember
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.focus.FocusRequester
import androidx.compose.ui.focus.focusRequester
import androidx.compose.ui.text.AnnotatedString
import androidx.compose.ui.text.input.ImeAction
import androidx.compose.ui.text.input.OffsetMapping
import androidx.compose.ui.text.input.TransformedText
import androidx.compose.ui.text.input.VisualTransformation
import androidx.compose.ui.text.style.TextAlign
import com.arkivanov.decompose.extensions.compose.subscribeAsState
import dev.icerock.moko.resources.compose.painterResource
import com.example.mystybag.core.ui.theme.LocalTheme
import com.example.mystybag.core.ui.theme.LocalTheme.p8
import com.example.mystybag.core.ui.theme.LocalTheme.p10
import com.example.mystybag.core.ui.theme.LocalTheme.p12
import com.example.mystybag.core.ui.theme.LocalTheme.p16
import com.example.mystybag.core.ui.theme.LocalTheme.p28
import com.example.mystybag.core.ui.theme.LocalTheme.p32
import com.example.mystybag.common.extensions.Image
import com.example.mystybag.common.extensions.doOnClick
import com.example.mystybag.common.extensions.keyboardPadding
import com.example.mystybag.common.logic.TextInputBottomSheetViewModel
import com.example.mystybag.common.ui.components.custom.DefaultCircleShapedTextFieldCursor
import com.example.mystybag.common.views.ActionSheetHeader
import com.example.mystybag.common.ui.components.native.DefaultButton
import com.example.mystybag.common.ui.components.native.Texts
import com.example.mystybag.core.views.AppTheme
import kotlinx.coroutines.delay
import kotlinx.coroutines.launch

@Composable
fun TextInputBottomSheetView(viewModel: TextInputBottomSheetViewModel) {
    val state           = viewModel.uiState.subscribeAsState()
    val focusRequester  = remember { FocusRequester() }

    Column(
        modifier            = Modifier
            .fillMaxWidth()
            .padding(horizontal = p16)
            .focusRequester(focusRequester)
            .keyboardPadding(),
        horizontalAlignment = Alignment.Start
    ) {
        ActionSheetHeader(
            title = state.value.title.ifBlank { null },
            showDivider = true,
            onCloseClick = viewModel::parentCloseBottomSheet
        )

        DefaultCircleShapedTextFieldCursor(
            label           = state.value.hint,
            modifier        = Modifier
                .fillMaxWidth()
                .padding(top = p32),
            value           = state.value.text,
            onValueChange   = viewModel::onTextChanged,
            inputType       = state.value.inputType,
            isError         = state.value.isError,
            errorText       = state.value.errorText,
            keyboardOptions = KeyboardOptions(
                keyboardType        = state.value.keyboardType,
                capitalization      = state.value.capitalization,
                autoCorrectEnabled  = state.value.autoCorrect,
                imeAction           = ImeAction.Done),
            trailingIcon    = state.value.imageResource?.let { resource ->
                {
                    Image(
                        painter     = painterResource(resource),
                        modifier    = Modifier
                            .size(LocalTheme.p28)
                            .doOnClick(viewModel::onIconClicked)
                    )
                }
            },
            visualTransformation =
                if (state.value.prefix != null || state.value.suffix != null)
                    VisualTransformation { value ->
                        TransformedText(
                            text          = AnnotatedString("${state.value.prefix ?: ""}${value.text}${state.value.suffix ?: ""}"),
                            offsetMapping = object : OffsetMapping {
                                private val prefix  : String?   = state.value.prefix
                                private val suffix  : String?   = state.value.suffix
                                private val text    : String    = value.text

                                override fun originalToTransformed(offset: Int) =
                                    when {
                                        prefix != null  -> offset + prefix.length
                                        else            -> offset
                                    }

                                override fun transformedToOriginal(offset: Int) =
                                    when {
                                        prefix != null && offset < prefix.length                                        -> 0
                                        (prefix != null && suffix != null && offset >= prefix.length + text.length) ||
                                        (prefix == null && suffix != null && offset >= text.length)                     -> text.length
                                        prefix != null                                                                  -> offset - prefix.length
                                        suffix != null                                                                  -> offset
                                        else                                                                            -> offset
                                    }
                            }
                        )
                    }
                else
                    VisualTransformation.None
        )

        // Info prompt / alert (e.g. change email verification message) – Figma: 380x72, cornerRadius 12, padding 12/16
        state.value.infoPromptText?.let { infoText ->
            Row(
                modifier = Modifier
                    .fillMaxWidth()
                    .padding(top = p32)
                    .clip(RoundedCornerShape(p12))
                    .background(AppTheme.current.colors.tertiaryContainer)
                    .padding(start = p12, top = p16, end = p16, bottom = p16),
                horizontalArrangement = Arrangement.spacedBy(p10),
                verticalAlignment = Alignment.Top
            ) {
                Box(
                    modifier = Modifier
                        .size(p28)
                        .clip(CircleShape)
                        .background(AppTheme.current.colors.tertiary),
                    contentAlignment = Alignment.Center
                ) {
                    Text(
                        text = "!",
                        color = AppTheme.current.colors.onTertiary,
                        style = AppTheme.current.typography.bodyMedium
                    )
                }
                Text(
                    text = infoText,
                    color = AppTheme.current.colors.onSurface,
                    style = AppTheme.current.typography.bodySmall,
                    modifier = Modifier.weight(1f)
                )
            }
        }

        DefaultButton(
            caption     = state.value.buttonText,
            modifier    = Modifier.padding(top = p32, bottom = p32),
            callback    = viewModel::onButtonClicked
        )
    }

    LaunchedEffect(Unit) {
        launch {
            delay(300)
            focusRequester.requestFocus()
        }
    }
}